import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBillingLifecycle } from "@/lib/billingLifecycle";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function SubscriptionAction({ action, label, tier, ...props }) {
  const { data, refresh } = useBillingLifecycle();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!data?.can_manage_billing) return null;
  const run = async () => {
    setBusy(true);
    try {
      if (action === "explore") {
        nav("/app/jobs");
      } else if (action === "trial_progress") {
        nav("/app/billing#trial-progress");
      } else if (action === "update_payment") {
        const response = await api.post("/subscription/portal", {});
        window.location.assign(response.data.url);
      } else if (action === "resume_subscription") {
        await api.post("/subscription/resume");
        await refresh();
        toast.success("Subscription resumed");
      } else if (action === "choose_plan" && tier && tier.monthly_price != null) {
        const response = await api.post("/subscription/checkout", { tier: tier.id });
        window.location.assign(response.data.url);
      } else if (action === "contact_support" || tier?.monthly_price === null) {
        nav("/app/support");
      } else {
        nav("/app/billing");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not complete this action. Please try again.");
    } finally { setBusy(false); }
  };
  return <Button {...props} disabled={busy || props.disabled} onClick={run} className="shrink-0 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-xs font-bold">{busy ? "Please wait…" : label}</Button>;
}

export function SubscriptionExpiryBanner() {
  const { data, trialValue } = useBillingLifecycle();
  if (!data || data.locked || data.lifecycle_state === "active") return null;
  const state = data.lifecycle_state;
  const days = data.trial_days_remaining;
  const manager = data.can_manage_billing;
  let title, body, label;
  let action = data.recommended_action;
  let urgent = false;
  if (state === "trial_active" || state === "trial_ending") {
    urgent = days <= 7;
    title = days > 7 ? `${data.plan_display_name} · ${days} days remaining`
      : days === 1 ? "Your Fieldlink trial ends in 1 day"
      : days <= 3 ? `Your Fieldlink trial ends in ${days} days`
      : `${days} days left in your Fieldlink trial`;
    body = !manager ? "Ask your company administrator to choose a plan to keep Fieldlink active."
      : days > 7 ? "Full access to Fieldlink during your trial."
      : days <= 3 ? "Keep your jobs, crew, timesheets and records running without interruption."
      : "Choose a plan to keep your team running.";
    const metric = trialValue?.metrics.find(m => m.key === "timesheets" && m.value > 0)
      || trialValue?.metrics.find(m => m.scope === "trial" && m.value > 0)
      || trialValue?.metrics.find(m => m.value > 0);
    if (manager && days > 3 && metric) {
      body = `${metric.value.toLocaleString("en-NZ")} ${metric.label.toLowerCase()} ${metric.scope === "trial" ? "during your trial" : "in your workspace today"}.`;
    } else if (manager && days > 7) {
      body = "Start running your jobs through Fieldlink to see everything in one place.";
    } else if (!manager) {
      title = `Fieldlink trial · ${days} ${days === 1 ? "day" : "days"} remaining`;
      body = days > 7 ? "Full access to Fieldlink during your company's trial." : "Ask your company administrator about continuing Fieldlink.";
    }
    label = days > 7 ? (metric ? "View trial progress" : "Explore Fieldlink") : "Choose a plan";
    if (days > 7) action = metric ? "trial_progress" : "explore";
  } else if (state === "payment_grace") {
    urgent = true;
    title = `Payment needs attention${manager ? ` · ${data.grace_days_remaining} days remaining` : ""}`;
    body = manager ? "We couldn't process your subscription. Your team can keep working during the grace period."
      : "Your company's subscription requires attention. Please contact your administrator.";
    label = data.recommended_action === "update_payment" ? "Update payment" : "Contact support";
  } else if (state === "cancelling") {
    title = data.current_period_end ? `Fieldlink subscription ends ${new Date(data.current_period_end).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" })}` : "Your subscription is scheduled to end";
    body = data.current_period_end ? "Your company will retain access until this date." : "Contact your administrator for subscription details.";
    label = data.recommended_action === "resume_subscription" ? "Resume subscription" : "Contact support";
  } else return null;
  return <aside aria-label="Subscription status" data-testid="subscription-lifecycle-banner" className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b border-l-4 px-4 py-2 lg:px-6 ${urgent ? (state === "trial_ending" && days <= 3 ? "border-orange-600 bg-orange-100 dark:bg-orange-950/50" : "border-orange-500 bg-orange-50 dark:bg-orange-950/30") : "border-[#FF5F15]/40 bg-stone-50 dark:bg-zinc-900"}`}>
    <div className="min-w-0 text-zinc-900 dark:text-zinc-100"><p className={`text-sm font-bold ${days <= 3 && state === "trial_ending" ? "text-orange-800 dark:text-orange-300" : ""}`}>{title}</p><p className="text-xs text-zinc-600 dark:text-zinc-400">{body}</p></div>
    <SubscriptionAction action={action} label={label} size="sm" />
  </aside>;
}
